import * as THREE from 'three/webgpu';

import { PROP, mergeConfig } from '../bake/BakeConfig.js';
import { OccluderSet } from '../bake/Occluders.js';
import { applyBake, bakeSync } from '../bake/bakeVertexLight.js';
import { assertNonIndexed, mergeNonIndexed, triCount } from '../build/mergeGeo.js';
// THE canonical hex -> linear conversion (CLAUDE.md). Every albedo in this file
// goes through it; a raw hex used as linear is ~2.2x too bright and the mistake
// is invisible until the car is on screen.
import { hexToLinear } from '../../levels/shared/palette.js';
import { STEER_MAX, liveryColor } from './carSpec.js';

/**
 * @file A {@link import('./carSpec.js').CarSpec} becomes a bag of rigid PARTS —
 * the same shape `actors/creatures.js#buildRig` produces for creatures, for the
 * same reason: one instanced draw per part, animated by a matrix, never by
 * touching a vertex.
 *
 * Ported from `procgen_cars/vehicle-gen.js#buildVehicle`, which built a
 * THREE.Group of ~181 meshes and ~3250 triangles with `MeshStandardMaterial`.
 * None of that survives contact with this engine (no lights, no shadow maps,
 * baked vertex colour only), so the port keeps the SHAPES and throws away the
 * scene graph.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE PART SET — this is the gameplay contract, not an implementation detail
 * ─────────────────────────────────────────────────────────────────────────────
 *   body            everything rigid and opaque, merged. Includes the boot lid /
 *                   tailgate (they never open in game), the pillars, the
 *                   bumpers, the mirrors, the steering COLUMN and the dark
 *                   ENGINE-BAY LINING the hood lids.
 *   hood            the bonnet PANEL, hinged at the rear (the base of the
 *                   windscreen) so a crash or a blast can pop it or tear it
 *                   off. It is CARVED OUT of the body, not laid on top: the
 *                   shell profile has always dipped to `bayFloor` between the
 *                   cowl and the nose, so with the hood gone you look into a
 *                   real recess. A car with no bonnet worth the name
 *                   (`carLayout#hasHood` false — a nose shorter than
 *                   {@link HOOD_MIN_BAY}) ships NO hood part and welds the
 *                   panel back into the body; every consumer looks it up BY
 *                   NAME and must tolerate its absence.
 *   glass           windscreen + backlight + fixed side/quarter glass. The ONE
 *                   transparent part, so a car never stacks two blended layers.
 *   wheel_fl/fr/rl/rr  hub-pivoted. Front pair steers, all four roll.
 *   door_fl/fr/rl/rr   hinged on the real hinge line (the door's FRONT edge),
 *                   +/-1.15 rad. Carjacking needs these; a one-row car
 *                   (`spec.rows === 1`) ships only the front pair.
 *   steering_wheel  column-axis pivot. Visible through the windscreen, so it
 *                   MUST turn with the steer (owner requirement).
 *   lamps           every lamp lens in the car, in one geometry, separated by a
 *                   per-vertex CHANNEL (see the table below).
 *
 * PRUNED at build (invisible from a 52-degree top-down camera, ~35% of the raw
 * triangle count): dashboard, binnacle, gauges, seats, pedals, shifter,
 * handbrake, tunnel, rear-view mirror, the engine bay's CONTENTS (block, cover,
 * air cleaner, battery, radiator), wipers, luggage, the SUV spare, the
 * wheel-well liners and the trunk / cargo / bed liners.
 *
 * ⚠ The bay's LINING came back with the detachable hood, and it is 10 triangles
 * because that is ALL the budget had: before it, the worst car (`van:37`) sat
 * at 2538, i.e. 62 triangles under `tests/vehicles.test.mjs`'s 2600. So the
 * five surfaces of the recess are five one-sided QUADS (`linerGeo`), not five
 * boxes (60 — over budget on its own) and not a solid tub. Everything the
 * player can see into is dark; everything they cannot see is not there.
 * The shipped worst car is now `van:37` at 2550, 50 under. That headroom is
 * the whole reason this is quads, so quote it as SPENT, not as spare.
 *
 * ⚠ The wheel-well liners are the one prune with a visible risk: upstream added
 * them to stop you seeing through the arch into the body. What actually closes
 * that hole here is the shell profile itself — `emitRun` detours the floor line
 * OVER each arch, so the extruded shell is solid above the opening — plus the
 * fenders at the front and the `sideWall` split at the rear. Verified by the
 * `archTopY` geometry, not by faith; re-check it in the gallery if you change
 * `archR`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * HOW THE RENDERER MUST COMPOSE A PART (the articulation contract)
 * ─────────────────────────────────────────────────────────────────────────────
 * Every part is placed with exactly this product, and nothing else:
 *
 *     world = Root(x, y, z, yaw) · T(rest.pos) · R_xyz(rest.rot) · R_y(a1) · R_z(a2)
 *
 *   • body / glass / lamps      a1 = 0,                    a2 = 0
 *   • wheel_f*                  a1 = steer · STEER_MAX,    a2 = roll · part.spin
 *   • wheel_r*                  a1 = 0,                    a2 = roll · part.spin
 *   • door_*                    a1 = open · part.openAngle,a2 = 0
 *   • steering_wheel            a1 = 0,                    a2 = −steer · 2
 *   • hood                      a1 = 0,                    a2 = pop · part.openAngle
 *
 * YAW THEN ROLL, in that order, both in the part's LOCAL frame — a steered wheel
 * that rolled first would cone out of its arch.
 *
 * ⚠ A DOOR AND A HOOD HINGE ABOUT DIFFERENT AXES, which is the whole reason the
 * contract carries two angles rather than one. A door swings about the car's
 * vertical (local Y, `a1`); a bonnet hinges about the axis ACROSS the car (local
 * Z, `a2`) — the same slot a wheel's roll and the steering wheel's spin already
 * take, so the hood needed no new algebra, only a new articulation CODE in the
 * fleet's `art` table. `a2 > 0` maps the part's local +X toward +Y, and the
 * hood's pivot is its REAR edge, so a positive pop lifts the FRONT of the panel
 * exactly the way a rear-hinged bonnet opens. A renderer that does not know
 * about hoods leaves both angles at 0 — which IS the shut bonnet, so the part
 * is safe to ship ahead of whatever animates it.
 *
 * ⚠ ALL FOUR WHEELS SHARE ONE GEOMETRY, so the fleet draws them in a single
 * batch. The shared geometry is authored for the RIGHT side (dish outboard at
 * +Z); the left pair carries `rest.ry = π`, which mirrors it correctly — a
 * proper rotation, never a negative scale (the winding trap). Conjugating a
 * roll by that π turns it into its own negative, which is why the left pair
 * also carries `spin = -1`: with it, all four wheels turn the same way in world
 * space. The consequence to know: the wheel is BAKED ONCE, at the front-right
 * corner, so the other three inherit that corner's shading. On a near-black
 * tyre at 52 degrees of pitch that is invisible; on anything shinier it would
 * not be.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE PAINT MASK — one archetype, many colours, no second bake
 * ─────────────────────────────────────────────────────────────────────────────
 * Painted panels (everything upstream drew with `mat.paint`: shell, fenders,
 * doors, roof, sails/pillars, hood, boot/gate, mirrors, roof box) bake with a
 * WHITE albedo and carry `aPaint = 1`. Everything else bakes its real colour
 * with `aPaint = 0`. The fleet material then does
 *
 *     colour = bakedDayNight × mix(vec3(1), instanceTint, aPaint)
 *
 * so one build serves every paint in {@link import('./carSpec.js').PAINTS}.
 *
 * A LIVERY (`spec.livery`) opts OUT of that: painted panels bake their literal
 * two-tone colour and carry `aPaint = 0`, which makes them immune to the
 * instance tint. That is how a police cruiser gets white doors/roof/hood over a
 * black shell and can never be repainted pink by a caller passing `{paint}`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * LAMP CHANNELS (`aLampChan`, constant per FACE)
 * ─────────────────────────────────────────────────────────────────────────────
 *   0  head        headlight lenses (2 or 4, `spec.x.quadLights`)
 *   1  brake/tail  the rear lenses — one channel, because the caller scales it
 *                  (~0.5 running, 1.0 braking) exactly the way upstream's two
 *                  registrations did
 *   2  signal_l    front AND rear indicator, LEFT (−Z, the driver's side)
 *   3  signal_r    front AND rear indicator, RIGHT (+Z)
 *   4  siren_red   lightbar, left half   (police / SWAT only)
 *   5  siren_blue  lightbar, right half  (police / SWAT only)
 *
 * The channel is a per-VERTEX float, identical across a triangle, and the
 * material decodes it in the VERTEX stage (CLAUDE.md's integer-decode rule).
 *
 * @module engine/vehicles/carBuild
 */

/** @typedef {import('./carSpec.js').CarSpec} CarSpec */

/**
 * @typedef {Object} CarPart
 * @property {string} name One of the part-set names above.
 * @property {'body'|'glass'|'wheel'|'door'|'steer'|'lamps'|'hood'} kind Drives
 *   which material the fleet gives it and which articulation applies. Anything
 *   that is not `glass` or `lamps` takes the shared BODY material — there are
 *   exactly three, and a hood is painted bodywork, so it needs no fourth.
 * @property {THREE.BufferGeometry} geometry Non-indexed, flat, baked
 *   (`color` + `color1`), plus `aPaint` and — on `lamps` — `aLampChan`. SHARED
 *   between the four wheel parts; `dispose()` frees each geometry once.
 * @property {{x:number,y:number,z:number,rx:number,ry:number,rz:number}} rest
 * @property {number} spin +1 / −1 for wheels (see the mirror note), 0 otherwise.
 * @property {number} openAngle The part's swing at full open, radians, SIGNED —
 *   a door's outward hinge ({@link DOOR_OPEN}, ± by side) or the hood's pop
 *   ({@link HOOD_OPEN}, always positive because a bonnet has no side). 0 for
 *   everything that does not open.
 * @property {boolean} front True for the steering pair.
 * @property {number} tris
 */

/**
 * Hard fence on parts per car — every part is a draw call per archetype.
 *
 * 13 since the bonnet became detachable: a two-row car ships body, hood, glass,
 * lamps, four wheels, four doors and the steering wheel, which is 13 parts and
 * TEN batches (the four wheels share one geometry, so they share one draw).
 * The city's ten civilian archetypes therefore move from an 88-batch ceiling to
 * ~98 — and it stays a CEILING rather than a frame cost, because per-instance
 * culling means an archetype with nothing on screen submits zero draws
 * (`docs/rendering.md`). The fence exists so a thirteenth part cannot land
 * quietly: raising it is a budget decision, not a refactor.
 */
export const MAX_CAR_PARTS = 13;

/** Lamp channel ids. Mirrored by `CarFleet`'s `setLamps`. */
export const LAMP = Object.freeze({
  HEAD: 0, BRAKE: 1, SIGNAL_L: 2, SIGNAL_R: 3, SIREN_RED: 4, SIREN_BLUE: 5,
});
/** Number of lamp channels — the width of the per-instance state. */
export const LAMP_CHANNELS = 6;

/** Door swing at full open, radians. Signed by side in {@link CarPart.openAngle}. */
export const DOOR_OPEN = 1.15;

/**
 * Bonnet lift at full pop, radians (~72°) — about the axis ACROSS the car, from
 * a pivot on the panel's REAR edge, so the front of the hood rises and the rear
 * stays on the cowl. Past 90° the panel would fall back over the windscreen; a
 * hood that has been TORN OFF is not this angle at all, it is a free body the
 * caller drives, and this constant is only the hinge's end stop.
 */
export const HOOD_OPEN = 1.25;

/**
 * The shortest engine bay that earns a hood part, metres — measured on the
 * shell's own bay run (`bayX1 − bayX0`), not on `spec.hoodLen`, because the run
 * is what actually opens as a recess. Under it the "bonnet" is a hand-sized
 * flap on a flat-nosed van and would cost a draw call to wobble; the panel is
 * welded into the body instead and `carLayout#hasHood` is false. Every shipped
 * archetype clears it today (the tightest is the van's ~0.29 m).
 */
const HOOD_MIN_BAY = 0.12;

/**
 * The car bake. Between `PROP` (3 m AO radius — a whole street) and `CREATURE`
 * (1.2 m — self-occlusion only): 1.6 m is the radius at which a wheel arch and
 * a mirror stalk pool shadow but the far flank does not darken the near one.
 * `dedupQuant` drops to 8 mm because a car's detail (a 12 mm door trim, a 20 mm
 * grille slat) is finer than the 20 mm default lattice, which would alias two
 * distinct lenses onto one lighting solve.
 */
export const CAR_BAKE = mergeConfig(PROP, {
  ao: { radius: 1.6, rays: 12, strength: 0.82, floor: 0.26 },
  sunRays: 2,
  sunMaxDist: 8,
  dedupQuant: 0.008,
  useWorker: false,
});

// ─────────────────────────────────────────────────────────────────────────────
// Albedos
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Every non-painted colour in the car, in LINEAR space. Upstream's material
 * table, minus the ones whose meshes are pruned.
 * @param {CarSpec} spec
 */
function albedos(spec) {
  return {
    dark: hexToLinear('#1b1c1f'),
    chrome: hexToLinear('#c9cdd3'),
    bumper: hexToLinear('#b6b9bd'),
    tire: hexToLinear('#141518'),
    rim: hexToLinear('#c7cad0'),
    inter: hexToLinear(spec.interior),
    interDark: hexToLinear('#3c3a36'),
    lightF: hexToLinear('#f4ecca'),
    signal: hexToLinear('#e0b33c'),
    lightR: hexToLinear('#a32418'),
    plate: hexToLinear('#e9e9e2'),
    glass: hexToLinear('#9ec7d8'),
    // Door glass is baked OPAQUE and dark: it rides the door part, and a second
    // transparent layer per car is exactly the coplanar-sort strobe CLAUDE.md
    // documents. Dark enough to read as tinted glass, light enough that an open
    // door still shows a window rather than a hole.
    doorGlass: hexToLinear('#1d242a'),
    sirenRed: hexToLinear('#7a1410'),
    sirenBlue: hexToLinear('#101f52'),
  };
}

/** Painted panels bake WHITE — the instance tint supplies the colour. */
const WHITE = /** @type {[number, number, number]} */ ([1, 1, 1]);

// ─────────────────────────────────────────────────────────────────────────────
// Layout — every derived dimension, in one place
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Turns a spec into the derived geometry it implies: axle lines, arch radii, the
 * door zone, the type-specific "stations" (boot front, cargo front, cab rear)
 * and the wheel track.
 *
 * EXPORTED because it is the only honest way to assert a pivot: a test that
 * re-derives `d.x1` from the spec is testing the spec, and a test that hard-codes
 * a number is testing nothing. Gameplay wants it too — a carjack needs to know
 * where a door is before it opens one.
 *
 * @param {CarSpec} spec
 */
export function carLayout(spec) {
  const s = spec;
  const L = s.L;
  const W = s.W;
  const g = s.g;
  const belt = s.beltY;
  const roof = s.roofY;
  const floorY = s.floorY;

  const nose = L / 2;
  const tail = -L / 2;
  const cowl = nose - s.hoodLen;
  const fa = nose - s.overF;
  const ra = tail + s.overR;

  // The arch must contain the tyre silhouette, and up front the tyre swept to
  // FULL LOCK: a turned wheel's shoulder reaches (wheelW/2)·sin(theta) further
  // fore/aft, which used to push tread through the fender. Sampling the whole
  // envelope also covers tall-tyre / low-clearance specs, where a fixed
  // `wheelR + 0.075` arch clipped the top of the tread.
  const archNeed = (a) => {
    const R0 = s.wheelR;
    const hw = s.wheelW / 2;
    const ca = Math.cos(a);
    const sn = Math.sin(a);
    let m = 0;
    for (let i = 0; i <= 24; i++) {
      const u = -R0 + (2 * R0 * i) / 24;
      const px = ca * Math.sqrt(Math.max(0, R0 * R0 - u * u)) + hw * sn;
      m = Math.max(m, Math.hypot(px, u + R0 - g));
    }
    return m;
  };
  const archR = Math.max(s.wheelR + 0.075, archNeed(0) + 0.05);
  const archRF = Math.max(archR, archNeed(STEER_MAX) + 0.05);

  const lidT = 0.045;
  const bayFloor = floorY + 0.10;
  const hoodRearY = belt - 0.05;
  const hoodFrontY = hoodRearY - s.hoodDrop;
  const cabTopHalf = W / 2 - 0.13;
  const roofFront = cowl - s.wsRun;

  // ── the bonnet and the bay it lids ─────────────────────────────────────────
  // The shell profile has ALWAYS dipped to `bayFloor` across `bayX0..bayX1`
  // (see `emitBody`'s `topPts`), so the engine bay is a recess the extrusion
  // already carves — the hood is its lid, not a slab on the nose. These four
  // numbers were spelled out inside `emitBody` and the hood emitter needs the
  // same ones; one definition is what keeps the lid and the hole the same size.
  const bayX0 = cowl + 0.02;
  const bayX1 = nose - 0.16;
  const hoodX0 = cowl + 0.01;
  const hoodX1 = nose - 0.005;
  // The panel runs 60 mm PAST the fenders' inner face (W/2 − 0.07) on each
  // side. That overlap is what stops daylight showing down the shut-line, and
  // it is why the hood may never be narrowed to the bay opening.
  const hoodHalfZ = W / 2 - 0.01;
  const hasHood = bayX1 - bayX0 >= HOOD_MIN_BAY;

  // ── door zone ──────────────────────────────────────────────────────────────
  const dzF = Math.min(cowl - 0.02, fa - archRF - 0.04);
  let dzR = ra + archR + 0.04;
  /** @type {number|null} */
  let bpX = null;
  /** @type {Array<{x0:number, x1:number, key:'f'|'r'}>} */
  let doors;
  if (s.rows === 2) {
    bpX = dzR + (dzF - dzR) * 0.46;
    doors = [
      { x0: bpX + 0.035, x1: dzF, key: 'f' },
      { x0: dzR, x1: bpX - 0.035, key: 'r' },
    ];
  } else {
    const w = Math.min(1.18, dzF - dzR);
    dzR = dzF - w;
    doors = [{ x0: dzR, x1: dzF, key: 'f' }];
  }

  // ── type stations ──────────────────────────────────────────────────────────
  /** @type {number|null} */
  let trunkF = null;
  /** @type {number|null} */
  let cargoF = null;
  /** @type {number|null} */
  let cabRear = null;
  let roofRear = 0;
  const deckY = belt - 0.03;
  const tubFloor = floorY + 0.16;
  const cargoFloor = floorY + 0.18;
  const bedFloor = floorY + 0.22;
  let rearTopY;
  if (s.type === 'sedan') {
    // Clamp: the backlight / roof end must never pass the rear door's rear edge
    // (dzR), or the sail quad self-intersects and the glass overhangs the corner.
    trunkF = tail + s.deckLen;
    roofRear = Math.min(trunkF + s.rgRun, dzR - 0.08);
    rearTopY = deckY - lidT;
  } else if (s.type === 'pickup') {
    cabRear = dzR - 0.14;
    roofRear = cabRear - 0.03;
    rearTopY = bedFloor;
  } else if (s.type === 'hatchback') {
    cargoF = Math.min(tail + s.cargoLen, dzR - 0.15);
    roofRear = tail + s.gateRun;
    rearTopY = cargoFloor;
  } else {
    // The roof runs nearly to the tail: boxy types get a vertical backlight.
    cargoF = Math.min(tail + s.cargoLen, dzR - 0.18);
    roofRear = tail + 0.01;
    rearTopY = cargoFloor;
  }

  return {
    L, W, g, belt, roof, floorY,
    nose, tail, cowl, fa, ra,
    archR, archRF,
    archRd: archR + 0.03,
    archRdF: archRF + 0.03,
    archTopY: g + archR + 0.04,
    lidT, bayFloor, hoodRearY, hoodFrontY, cabTopHalf, roofFront,
    bayX0, bayX1, hoodX0, hoodX1, hoodHalfZ, hasHood,
    cabCutY: floorY + 0.02,
    glassW0: W / 2 - 0.10,
    dzF, dzR, bpX, doors,
    trunkF, cargoF, cabRear, roofRear,
    deckY, tubFloor, cargoFloor, bedFloor, rearTopY,
    /** Wheel centre |z|. */
    track: W / 2 - s.wheelW / 2 - 0.015,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Geometry primitives (ports of upstream's `solid` / `bx` / `cyl`, mesh-free)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * De-indexes and flat-shades. On non-indexed geometry `computeVertexNormals`
 * writes the FACE normal to all three vertices, which is exactly what rule 3
 * asks for.
 * @param {THREE.BufferGeometry} geo Consumed — disposed if a copy was made.
 * @returns {THREE.BufferGeometry}
 */
function flat(geo) {
  let g = geo;
  if (g.index !== null) {
    g = g.toNonIndexed();
    geo.dispose();
  }
  g.computeVertexNormals();
  return g;
}

/**
 * A hexahedron from two 4-point loops (outer surface, inner surface), winding
 * auto-fixed OUTWARD from the centroid — upstream's workhorse for panels that
 * are neither boxes nor extrusions (pillars, sails, glass, the hood).
 *
 * @param {Array<[number,number,number]|number[]>} o Outer loop, 4 points.
 * @param {Array<[number,number,number]|number[]>} i Inner loop, 4 points.
 * @returns {THREE.BufferGeometry}
 */
function solidGeo(o, i) {
  const c = [...o, ...i];
  const faces = [[0, 1, 2, 3], [4, 7, 6, 5], [0, 4, 5, 1], [1, 5, 6, 2], [2, 6, 7, 3], [3, 7, 4, 0]];
  const cen = [0, 1, 2].map((k) => c.reduce((a, p) => a + p[k], 0) / 8);
  /** @type {number[]} */
  const pos = [];
  for (const f of faces) {
    const p = f.map((j) => c[j]);
    const u = [p[1][0] - p[0][0], p[1][1] - p[0][1], p[1][2] - p[0][2]];
    const v = [p[2][0] - p[0][0], p[2][1] - p[0][1], p[2][2] - p[0][2]];
    const n = [u[1] * v[2] - u[2] * v[1], u[2] * v[0] - u[0] * v[2], u[0] * v[1] - u[1] * v[0]];
    const fc = [0, 1, 2].map((k) => (p[0][k] + p[1][k] + p[2][k] + p[3][k]) / 4);
    const d = n[0] * (fc[0] - cen[0]) + n[1] * (fc[1] - cen[1]) + n[2] * (fc[2] - cen[2]);
    const idx = d >= 0 ? [0, 1, 2, 0, 2, 3] : [0, 2, 1, 0, 3, 2];
    for (const j of idx) pos.push(p[j][0], p[j][1], p[j][2]);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.computeVertexNormals();
  return assertNonIndexed(geo, 'solidGeo');
}

/**
 * One-sided quads with an EXPLICITLY GIVEN facing, merged into one piece.
 *
 * `solidGeo` winds each face outward from the hexahedron's own centroid, which
 * is exactly wrong for a CONCAVE surface like the engine bay's lining: outward
 * there points INTO the surrounding bodywork, and every quad would be culled at
 * the one angle it exists for. Each entry therefore names the direction the
 * face has to be visible from and the winding follows it.
 *
 * One-sided and ONE piece on purpose. Backs are never seen (they are buried in
 * the shell), a 5-quad tub is 10 triangles where 5 boxes would be 60, and one
 * piece is one occluder box in the bake instead of five.
 *
 * @param {Array<{n: number[], q: Array<number[]>}>} faces `n` is the outward
 *   direction (need not be unit); `q` is four corners in either winding.
 * @returns {THREE.BufferGeometry}
 */
function linerGeo(faces) {
  /** @type {number[]} */
  const pos = [];
  for (const { n, q } of faces) {
    const u = [q[1][0] - q[0][0], q[1][1] - q[0][1], q[1][2] - q[0][2]];
    const v = [q[2][0] - q[0][0], q[2][1] - q[0][1], q[2][2] - q[0][2]];
    const c = [u[1] * v[2] - u[2] * v[1], u[2] * v[0] - u[0] * v[2], u[0] * v[1] - u[1] * v[0]];
    const idx = c[0] * n[0] + c[1] * n[1] + c[2] * n[2] >= 0 ? [0, 1, 2, 0, 2, 3] : [0, 2, 1, 0, 3, 2];
    for (const j of idx) pos.push(q[j][0], q[j][1], q[j][2]);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.computeVertexNormals();
  return assertNonIndexed(geo, 'linerGeo');
}

/**
 * @param {number} w @param {number} h @param {number} d
 * @param {number} x @param {number} y @param {number} z
 * @param {number} [rz] Rotation about the box's OWN centre, applied before the
 *   translation — upstream set `mesh.rotation.z` on a positioned mesh, which is
 *   the same order (`T · R`).
 * @returns {THREE.BufferGeometry}
 */
function boxGeo(w, h, d, x, y, z, rz = 0) {
  const g = new THREE.BoxGeometry(w, h, d);
  if (rz) g.rotateZ(rz);
  g.translate(x, y, z);
  return flat(g);
}

/**
 * @param {number} r @param {number} len
 * @param {number} x @param {number} y @param {number} z
 * @param {'x'|'y'|'z'} [axis='z']
 * @param {number} [seg=16]
 * @param {boolean} [open=false] Drops both end caps (2·seg tris) for cylinders
 *   whose ends are buried in other parts.
 * @param {number} [rz] Extra rotation about Z, applied before the translation.
 * @returns {THREE.BufferGeometry}
 */
function cylGeo(r, len, x, y, z, axis = 'z', seg = 16, open = false, rz = 0) {
  const g = new THREE.CylinderGeometry(r, r, len, seg, 1, open);
  if (axis === 'z') g.rotateX(Math.PI / 2);
  else if (axis === 'x') g.rotateZ(Math.PI / 2);
  if (rz) g.rotateZ(rz);
  g.translate(x, y, z);
  return flat(g);
}

// ─────────────────────────────────────────────────────────────────────────────
// The accumulator
// ─────────────────────────────────────────────────────────────────────────────

/**
 * One emitted piece, before merging. `zone` and `rgb` are mutually exclusive:
 * a zone means "this is paint, resolve it against the livery", an rgb means
 * "this is its own colour".
 * @typedef {Object} Piece
 * @property {THREE.BufferGeometry} geo
 * @property {[number,number,number]|null} rgb
 * @property {'shell'|'door'|'roof'|'hood'|null} zone
 * @property {number} lamp Channel, or −1.
 * @property {boolean} [occ] False to keep this piece OUT of the occluder set —
 *   see {@link CarAcc#addNonOccluding}. Defaults to true.
 */

class CarAcc {
  constructor() {
    /** @type {Map<string, Piece[]>} */
    this.parts = new Map();
  }

  /**
   * @param {string} part
   * @param {THREE.BufferGeometry} geo
   * @param {[number,number,number]} rgb LINEAR albedo.
   * @param {number} [lamp=-1]
   */
  add(part, geo, rgb, lamp = -1) {
    this._push(part, { geo, rgb, zone: null, lamp });
  }

  /**
   * Like {@link CarAcc#add}, but the piece contributes NO occluder box.
   *
   * ⚠ Reach for this only for a CONCAVE piece that lines a cavity some other
   * piece's box already fills, and never for solid bodywork. The bake takes one
   * AABB per piece, so the engine bay's hollow lining would enter it as a SOLID
   * BLOCK filling the whole nose: measured against the pre-hood build, adding
   * that box moved the day channel by up to 0.65 on the front WHEEL, 0.10 on
   * the lamps and 0.02 on the windscreen — a car that never opens its bonnet
   * would have been re-shaded from top to bottom by a surface nobody can see.
   * The volume is already occluded by the shell's own box, so dropping this one
   * loses nothing and keeps an intact car bit-for-bit what it was.
   *
   * @param {string} part
   * @param {THREE.BufferGeometry} geo
   * @param {[number,number,number]} rgb LINEAR albedo.
   */
  addNonOccluding(part, geo, rgb) {
    this._push(part, { geo, rgb, zone: null, lamp: -1, occ: false });
  }

  /**
   * Paint: white + `aPaint = 1`, unless a livery claims the zone.
   * @param {string} part
   * @param {THREE.BufferGeometry} geo
   * @param {'shell'|'door'|'roof'|'hood'} zone
   */
  paint(part, geo, zone) {
    this._push(part, { geo, rgb: null, zone, lamp: -1 });
  }

  /** @param {string} part @param {Piece} piece */
  _push(part, piece) {
    let list = this.parts.get(part);
    if (!list) this.parts.set(part, (list = []));
    list.push(piece);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// The build
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Builds, prunes, merges and BAKES one car.
 *
 * Deterministic: same spec ⇒ byte-identical geometry. `buildVehicle` upstream
 * opened a second rng stream (`seed ^ 0x9e3779b9`) purely to scatter luggage in
 * the boot; that content is pruned, so the stream is gone and the build takes no
 * random numbers at all.
 *
 * @param {CarSpec} spec
 * @returns {{parts: CarPart[], info: Object, tris: number, dispose: () => void}}
 *   `tris` is the DRAWN cost of one car (a shared wheel geometry counts four
 *   times), which is the number a budget cares about.
 */
export function buildCarParts(spec) {
  const LY = carLayout(spec);
  const A = albedos(spec);
  const acc = new CarAcc();

  emitBody(spec, LY, A, acc);
  emitHood(spec, LY, A, acc);
  emitRear(spec, LY, A, acc);
  emitGreenhouse(spec, LY, A, acc);
  emitDoors(spec, LY, A, acc);
  emitFascia(spec, LY, A, acc);
  emitFurniture(spec, LY, A, acc);
  const swRest = emitSteering(spec, LY, A, acc);
  emitWheel(spec, LY, A, acc);
  if (spec.lightbar) emitLightbar(spec, LY, A, acc);

  // ── placements ─────────────────────────────────────────────────────────────
  // `geoKey` is the accumulator bucket; several parts may share one (the wheels).
  const T = LY.track;
  /** @type {Array<{name: string, kind: CarPart['kind'], geoKey: string, rest: any, spin: number, openAngle: number, front: boolean, bake: boolean}>} */
  const placements = [
    { name: 'body', kind: 'body', geoKey: 'body', rest: rest0(), spin: 0, openAngle: 0, front: false, bake: true },
    // The hood hinges on the panel's REAR EDGE — the base of the windscreen —
    // through the middle of its 45 mm thickness, so the hinge line is inside
    // the metal rather than skimming its skin. `hasHood` is the flat-nose gate;
    // when it is false there is no part here at all and `emitHood` has already
    // welded the panel into the body (every consumer looks the hood up BY NAME).
    ...(LY.hasHood ? [{
      name: 'hood',
      kind: /** @type {CarPart['kind']} */ ('hood'),
      geoKey: 'hood',
      rest: rest0(LY.hoodX0, LY.hoodRearY - LY.lidT / 2, 0),
      spin: 0,
      openAngle: HOOD_OPEN,
      front: false,
      bake: true,
    }] : []),
    { name: 'glass', kind: 'glass', geoKey: 'glass', rest: rest0(), spin: 0, openAngle: 0, front: false, bake: true },
    { name: 'lamps', kind: 'lamps', geoKey: 'lamps', rest: rest0(), spin: 0, openAngle: 0, front: false, bake: true },
    // The bake happens at the FRONT-RIGHT corner; the other three inherit it.
    { name: 'wheel_fr', kind: 'wheel', geoKey: 'wheel', rest: rest0(LY.fa, spec.wheelR, T), spin: 1, openAngle: 0, front: true, bake: true },
    { name: 'wheel_fl', kind: 'wheel', geoKey: 'wheel', rest: rest0(LY.fa, spec.wheelR, -T, 0, Math.PI, 0), spin: -1, openAngle: 0, front: true, bake: false },
    { name: 'wheel_rr', kind: 'wheel', geoKey: 'wheel', rest: rest0(LY.ra, spec.wheelR, T), spin: 1, openAngle: 0, front: false, bake: false },
    { name: 'wheel_rl', kind: 'wheel', geoKey: 'wheel', rest: rest0(LY.ra, spec.wheelR, -T, 0, Math.PI, 0), spin: -1, openAngle: 0, front: false, bake: false },
    { name: 'steering_wheel', kind: 'steer', geoKey: 'steering_wheel', rest: swRest, spin: 0, openAngle: 0, front: false, bake: true },
  ];
  const hz = spec.W / 2 - 0.03;
  for (const d of LY.doors) {
    for (const sd of [-1, 1]) {
      const name = `door_${d.key}${sd < 0 ? 'l' : 'r'}`;
      placements.push({
        name,
        kind: 'door',
        geoKey: name,
        // The hinge is the door's FRONT edge, at car x = d.x1 (upstream's
        // `hinged(car, d.x1, 0, hz)`), on the door's own z plane.
        rest: rest0(d.x1, 0, sd * hz),
        spin: 0,
        openAngle: sd < 0 ? -DOOR_OPEN : DOOR_OPEN,
        front: d.key === 'f',
        bake: true,
      });
    }
  }
  if (placements.length > MAX_CAR_PARTS) {
    throw new Error(`buildCarParts(${spec.type}): ${placements.length} parts > MAX_CAR_PARTS ${MAX_CAR_PARTS}`);
  }

  // Parts authored in CAR space are moved into part-local space by subtracting
  // the pivot; parts authored locally (the wheel, the steering wheel) already
  // are. Rest ROTATIONS are never folded into geometry — a rotated pivot must
  // stay a pivot.
  const CAR_SPACE = new Set(['body', 'glass', 'lamps', 'hood', ...LY.doors.flatMap((d) => [`door_${d.key}l`, `door_${d.key}r`])]);

  // ── occluders: one box per PIECE, in car space ─────────────────────────────
  // NOT one box per part: the body's own bounding box is the whole car, and an
  // occluder that big turns the bake into a uniform grey. Per piece, the union
  // is a decent stand-in for the car's real solid volume.
  const occ = new OccluderSet();
  const restM = new THREE.Matrix4();
  const box = new THREE.Box3();
  for (const p of placements) {
    const pieces = acc.parts.get(p.geoKey);
    if (!pieces) continue;
    const local = !CAR_SPACE.has(p.geoKey);
    matrixOf(restM, p.rest);
    for (const piece of pieces) {
      if (piece.occ === false) continue; // a hollow lining — see `addNonOccluding`
      piece.geo.computeBoundingBox();
      box.copy(/** @type {THREE.Box3} */ (piece.geo.boundingBox));
      if (local) box.applyMatrix4(restM);
      occ.addBox({ x: box.min.x, y: box.min.y, z: box.min.z }, { x: box.max.x, y: box.max.y, z: box.max.z });
    }
  }
  occ.build({ cell: 0.35 });

  // ── merge + bake ───────────────────────────────────────────────────────────
  /** @type {Map<string, THREE.BufferGeometry>} */
  const geos = new Map();
  const worldBox = new THREE.Box3();
  worldBox.makeEmpty();

  for (const p of placements) {
    if (geos.has(p.geoKey)) continue;
    const pieces = acc.parts.get(p.geoKey) ?? [];
    const geo = mergePieces(pieces, spec, p.geoKey === 'lamps');
    if (CAR_SPACE.has(p.geoKey)) geo.translate(-p.rest.x, -p.rest.y, -p.rest.z);
    geos.set(p.geoKey, geo);
  }

  for (const p of placements) {
    if (!p.bake) continue;
    const geo = /** @type {THREE.BufferGeometry} */ (geos.get(p.geoKey));
    if (geo.getAttribute('position').count === 0) continue;
    // Bake in CAR space (positions and normals are what AO and N·L see), then
    // carry the two channels back onto the part-LOCAL geometry — same vertex
    // order, so the arrays transplant directly. This is the trade the engine
    // makes everywhere: an opening door keeps its rest-pose shading.
    const cs = geo.clone();
    cs.applyMatrix4(matrixOf(restM, p.rest));
    applyBake(geo, bakeSync(cs, occ, null, CAR_BAKE));
    cs.dispose();
  }

  /** @type {CarPart[]} */
  const parts = [];
  let tris = 0;
  for (const p of placements) {
    const geo = /** @type {THREE.BufferGeometry} */ (geos.get(p.geoKey));
    geo.computeBoundingBox();
    geo.computeBoundingSphere();
    const t = triCount(geo);
    tris += t;
    worldBox.union(box.copy(/** @type {THREE.Box3} */ (geo.boundingBox)).applyMatrix4(matrixOf(restM, p.rest)));
    parts.push({
      name: p.name,
      kind: p.kind,
      geometry: geo,
      rest: p.rest,
      spin: p.spin,
      openAngle: p.openAngle,
      front: p.front,
      tris: t,
    });
  }

  for (const pieces of acc.parts.values()) for (const piece of pieces) piece.geo.dispose();

  const info = {
    type: spec.type,
    seed: spec.seed,
    variant: spec.variant ?? spec.type,
    livery: spec.livery ?? null,
    lightbar: !!spec.lightbar,
    L: spec.L,
    W: spec.W,
    H: worldBox.max.y,
    /** The spec's own paint — `CarFleet`'s default instance tint. */
    paint: spec.paint,
    /** Rolling radius: `CarFleet#advance` turns metres into wheel radians with it. */
    wheelR: spec.wheelR,
    rows: spec.rows,
    doors: LY.doors.length * 2,
    layout: LY,
    bbox: {
      minX: worldBox.min.x, maxX: worldBox.max.x,
      minY: worldBox.min.y, maxY: worldBox.max.y,
      minZ: worldBox.min.z, maxZ: worldBox.max.z,
    },
  };

  let disposed = false;
  return {
    parts,
    info,
    tris,
    dispose() {
      if (disposed) return;
      disposed = true;
      // One dispose per GEOMETRY, not per part — the wheels share theirs.
      for (const geo of geos.values()) geo.dispose();
      geos.clear();
    },
  };
}

/**
 * @param {number} [x] @param {number} [y] @param {number} [z]
 * @param {number} [rx] @param {number} [ry] @param {number} [rz]
 */
function rest0(x = 0, y = 0, z = 0, rx = 0, ry = 0, rz = 0) {
  return { x, y, z, rx, ry, rz };
}

const _euler = new THREE.Euler();

/**
 * `T(pos) · R_xyz(rot)` — the rest half of the articulation contract.
 * @param {THREE.Matrix4} m Scratch.
 * @param {{x:number,y:number,z:number,rx:number,ry:number,rz:number}} r
 * @returns {THREE.Matrix4} `m`
 */
function matrixOf(m, r) {
  m.makeRotationFromEuler(_euler.set(r.rx, r.ry, r.rz, 'XYZ'));
  m.setPosition(r.x, r.y, r.z);
  return m;
}

/**
 * Merges one part's pieces and attaches the per-vertex channel the part's
 * material reads.
 *
 * `mergeNonIndexed` carries position/normal/uv/color and asserts the result is
 * non-indexed (rule 3); `aPaint` / `aLampChan` are appended here because they
 * are ours, not the merge contract's. Both are written per SOURCE PIECE, so they
 * are constant across every triangle of that piece — which is what makes the
 * lamp channel safe to decode and the paint mask safe to interpolate.
 *
 * ⚠ WEBGPU BINDS AT MOST 8 VERTEX BUFFERS PER PIPELINE, and a part already
 * spends 5 of them (`position`, `color`, `color1`, `instanceMatrix`, plus its
 * own channel) before the fleet adds its instanced attributes. Two consequences
 * live here and both are load-bearing:
 *
 *  • `uv` is DELETED. A car samples no texture, so it was dead weight that also
 *    cost a slot.
 *  • `aPaint` and `aLampChan` are MUTUALLY EXCLUSIVE. A lamp lens is never a
 *    painted panel (asserted below), so the lamps part carries only the channel
 *    and the fleet gives it a material with no paint tint at all. Attaching both
 *    to both is what pushed the lamp pipeline to 10 buffers and blanked the
 *    whole frame — the validation error names the pipeline, not the cause.
 *
 * @param {Piece[]} pieces
 * @param {CarSpec} spec
 * @param {boolean} withLamp This is the `lamps` part.
 * @returns {THREE.BufferGeometry}
 */
function mergePieces(pieces, spec, withLamp) {
  const live = pieces.filter((p) => (p.geo.getAttribute('position')?.count ?? 0) > 0);
  // Resolve paint zones BEFORE the merge — `mergeNonIndexed` reads `color`.
  for (const p of live) {
    let rgb = p.rgb;
    let paint = 0;
    if (p.zone) {
      const hex = liveryColor(spec.livery ?? null, p.zone);
      if (hex) rgb = hexToLinear(hex); // a livery is literal: NOT instance-tintable
      else {
        rgb = WHITE;
        paint = 1;
      }
    }
    tintPiece(p.geo, /** @type {[number,number,number]} */ (rgb ?? WHITE));
    p._paint = paint;
  }

  if (withLamp && live.some((p) => p._paint !== 0)) {
    throw new Error('mergePieces: a lamp lens was emitted as a PAINTED panel — the lamps part carries no paint tint');
  }

  const merged = mergeNonIndexed(live.map((p) => p.geo));
  merged.deleteAttribute('uv'); // untextured, and a vertex-buffer slot we cannot spare
  const total = merged.getAttribute('position').count;
  const chanArr = new Float32Array(total);
  let off = 0;
  for (const p of live) {
    const n = p.geo.getAttribute('position').count;
    chanArr.fill(withLamp ? p.lamp : p._paint, off, off + n);
    off += n;
  }
  if (off !== total) {
    throw new Error(`mergePieces: wrote ${off} of ${total} vertices — merge order drifted`);
  }
  merged.setAttribute(withLamp ? 'aLampChan' : 'aPaint', new THREE.BufferAttribute(chanArr, 1));
  return assertNonIndexed(merged, 'car part');
}

/**
 * Writes a flat linear-albedo `color` attribute — the bake's input.
 * @param {THREE.BufferGeometry} geo
 * @param {[number,number,number]} rgb
 */
function tintPiece(geo, rgb) {
  const n = geo.getAttribute('position').count;
  const arr = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    arr[i * 3] = rgb[0];
    arr[i * 3 + 1] = rgb[1];
    arr[i * 3 + 2] = rgb[2];
  }
  geo.setAttribute('color', new THREE.BufferAttribute(arr, 3));
}

// ─────────────────────────────────────────────────────────────────────────────
// Emitters — the port proper. Everything below is upstream's geometry, minus
// the pruned meshes, routed into parts instead of a scene graph.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The bonnet's TOP line at car x — ONE definition, because three things have to
 * agree on it to the millimetre: the hood panel itself, the fenders that tuck
 * 4 mm under its edge, and the bay lining that must clear its underside.
 * @param {ReturnType<carLayout>} LY @param {number} x
 * @returns {number}
 */
function hoodTopY(LY, x) {
  return LY.hoodRearY + (LY.hoodFrontY - LY.hoodRearY) * (x - LY.cowl) / (LY.nose - LY.cowl);
}

/**
 * Body shell (extruded profile), fenders, cabin structure. The bonnet PANEL
 * left with {@link emitHood} when it became detachable; what stays here is the
 * recess it lids, which the shell profile has carved all along.
 * @param {CarSpec} s @param {ReturnType<carLayout>} LY @param {any} A @param {CarAcc} acc
 */
function emitBody(s, LY, A, acc) {
  const { W, g, belt, floorY, nose, tail, cowl, fa, ra, archR, archRF, archRd, archRdF,
    lidT, bayFloor, hoodFrontY, cabCutY, dzF, dzR, bpX, bayX0, bayX1,
    trunkF, cargoF, cabRear, deckY, tubFloor, cargoFloor, bedFloor } = LY;

  // ── profile ────────────────────────────────────────────────────────────────
  /** @type {any[][]} */
  const topPts = [];
  if (s.type === 'sedan') {
    topPts.push([tail, deckY - lidT], [tail + 0.12, deckY - lidT], ['run', tail + 0.12, trunkF + 0.02, tubFloor],
      [trunkF + 0.02, deckY - lidT], [trunkF - 0.03, deckY - lidT], [trunkF - 0.03, belt]);
  } else if (s.type === 'pickup') {
    const bedFront = cabRear + 0.06;
    topPts.push(['run', tail, bedFront, bedFloor], [bedFront, belt]);
  } else {
    topPts.push(['run', tail, cargoF, cargoFloor], [cargoF, belt]);
  }
  // Cabin tub: open the shell across the whole door zone so the interior is
  // hollow — it used to run solid to the belt here, burying everything inside.
  topPts.push([dzR, belt], [dzR, cabCutY], [dzF, cabCutY], [dzF, belt], [cowl, belt]);
  topPts.push(['run', cowl, nose - 0.16, bayFloor], [nose - 0.16, hoodFrontY - lidT], [nose, hoodFrontY - lidT]);

  // Floor cuts must detour OVER the wheel arches: a straight low floor line
  // crossing an arch arc makes the profile polygon self-intersect and earcut
  // punches holes in the shell. `['run', xA, xB, fy]` follows the upper envelope
  // of y = fy and the arch circles (radius archR + 0.03 -> a 3 cm paint lip).
  const rdOf = (cx) => (cx === fa ? archRdF : archRd);
  /** @param {THREE.Shape} sh @param {number} xA @param {number} xB @param {number} fy */
  function emitRun(sh, xA, xB, fy) {
    const circY = (cx, x) => {
      const r = rdOf(cx);
      const d = x - cx;
      return Math.abs(d) < r ? g + Math.sqrt(r * r - d * d) : -Infinity;
    };
    sh.lineTo(xA, Math.max(fy, circY(ra, xA), circY(fa, xA)));
    let x = xA;
    for (const cx of [ra, fa]) {
      const r = rdOf(cx);
      if (fy >= g + r || cx + r <= xA || cx - r >= xB) continue;
      const dx = Math.sqrt(r * r - (fy - g) * (fy - g));
      const xin = Math.max(xA, cx - dx);
      const xout = Math.min(xB, cx + dx);
      if (xout <= xin) continue;
      if (xin > x) sh.lineTo(xin, fy);
      sh.absarc(cx, g, r, Math.acos((xin - cx) / r), Math.acos((xout - cx) / r), true);
      x = xout;
    }
    if (x < xB) sh.lineTo(xB, fy);
  }

  const shape = new THREE.Shape();
  shape.moveTo(nose, g);
  shape.lineTo(fa + archRF, g);
  shape.absarc(fa, g, archRF, 0, Math.PI, false);
  shape.lineTo(ra + archR, g);
  shape.absarc(ra, g, archR, 0, Math.PI, false);
  shape.lineTo(tail, g);
  for (const p of topPts) {
    if (p[0] === 'run') emitRun(shape, p[1], p[2], p[3]);
    else shape.lineTo(p[0], p[1]);
  }
  shape.closePath();
  const shellGeo = new THREE.ExtrudeGeometry(shape, { depth: W, bevelEnabled: false, curveSegments: 8 });
  shellGeo.translate(0, 0, -W / 2);
  acc.paint('body', flat(shellGeo), 'shell');

  // ── cabin structure ────────────────────────────────────────────────────────
  // The shell is tub-cut across the door zone, so these are what stop you seeing
  // straight through the cabin into the boot and the bay from a 52-degree camera.
  const cabFloorX0 = (s.type === 'pickup') ? cabRear + 0.02 : (s.type === 'sedan' ? trunkF : cargoF) + 0.02;
  acc.add('body', boxGeo(cowl - cabFloorX0, 0.035, W - 0.18, (cowl + cabFloorX0) / 2, floorY + 0.005, 0), A.inter);
  if (bpX != null) {
    for (const sd of [-1, 1]) {
      acc.paint('body', boxGeo(0.14, belt - cabCutY, 0.06, bpX + 0.005, (belt + cabCutY) / 2, sd * (W / 2 - 0.03)), 'shell');
    }
  }
  acc.add('body', boxGeo(0.02, belt - cabCutY - 0.02, W - 0.26, dzR + 0.012, (belt + cabCutY) / 2, 0), A.inter);
  acc.add('body', boxGeo(0.02, belt - cabCutY - 0.02, W - 0.26, dzF - 0.012, (belt + cabCutY) / 2, 0), A.interDark);

  // ── fenders ────────────────────────────────────────────────────────────────
  // (The bay CONTENTS are pruned; the fenders are exterior bodywork and stay.)
  const hoodYat = (x) => hoodTopY(LY, x);
  // The fender bottom hugs the front-arch detour arc: a flat bottom at bayFloor
  // hangs a paint slab over the front wheel opening.
  const dyf = Math.min(archRdF, Math.max(0, bayFloor - g));
  const dxf = Math.sqrt(archRdF * archRdF - dyf * dyf);
  const fBotY = (x) => {
    const dd = Math.abs(x - fa);
    return dd >= dxf ? bayFloor : g + Math.sqrt(archRdF * archRdF - dd * dd) - 0.02;
  };
  // Breakpoints pinned to the flat->arc crossings (fa +/- dxf): uniform sampling
  // let a chord bridge the corner and open a triangular gap beside the arch.
  const fbps = [bayX0, bayX1];
  const fs0 = Math.max(bayX0, fa - dxf);
  const fs1 = Math.min(bayX1, fa + dxf);
  if (fs1 > fs0 + 1e-4) {
    for (let k = 0; k <= 6; k++) {
      const x = fs0 + ((fs1 - fs0) * k) / 6;
      if (x > bayX0 + 1e-4 && x < bayX1 - 1e-4) fbps.push(x);
    }
  }
  fbps.sort((p, q) => p - q);
  for (const sd of [-1, 1]) {
    const zo = sd * (W / 2);
    const zi = sd * (W / 2 - 0.07);
    for (let k = 0; k + 1 < fbps.length; k++) {
      const xa = fbps[k];
      const xb = fbps[k + 1];
      const yA = fBotY(xa);
      const yB = fBotY(xb);
      const tA = hoodYat(xa) - lidT - 0.004;
      const tB = hoodYat(xb) - lidT - 0.004;
      if (Math.min(tA - yA, tB - yB) < 0.015) continue;
      acc.paint('body', solidGeo(
        [[xa, yA, zo], [xb, yB, zo], [xb, tB, zo], [xa, tA, zo]],
        [[xa, yA, zi], [xb, yB, zi], [xb, tB, zi], [xa, tA, zi]],
      ), 'shell');
    }
  }
}

/**
 * The BONNET — its own part, hinged at the rear — and the dark lining of the
 * recess underneath it.
 *
 * The panel geometry is byte for byte what `emitBody` used to weld into the
 * shell; all that changed is which bucket it lands in and that the pivot is
 * subtracted from it afterwards (`CAR_SPACE`). That is deliberate: an intact
 * car has to look EXACTLY as it did, so the shut-line, the 60 mm overlap onto
 * the fenders and the 4 mm the fender tops sit under the panel edge are all
 * untouched numbers.
 *
 * ⚠ THE LINING IS THE POINT OF THE CARVE. Without it a torn-off hood exposes
 * the shell's own bay floor and the fenders' inner faces — every one of them a
 * PAINTED panel — so the hole under the bonnet would come out body-coloured and
 * read as a modelling mistake rather than an engine bay. It reuses `A.dark`,
 * the near-black the grille, the B pillars and the rocker already carry: the
 * fleet has exactly THREE shared materials and a hood is bodywork, so neither a
 * fourth material nor a fourth albedo is on the table.
 *
 * @param {CarSpec} s @param {ReturnType<carLayout>} LY @param {any} A @param {CarAcc} acc
 */
function emitHood(s, LY, A, acc) {
  const { W, g, fa, archRdF, lidT, bayFloor, hoodRearY, hoodFrontY, bayX0, bayX1, hoodX0, hoodX1, hoodHalfZ, hasHood } = LY;
  const hw = hoodHalfZ;

  // A flat-nosed car keeps the panel welded into the shell, where it has always
  // been: a hood part that can never open is a draw call and a detachment
  // nobody can see.
  acc.paint(hasHood ? 'hood' : 'body', solidGeo(
    [[hoodX0, hoodRearY - lidT, -hw], [hoodX1, hoodFrontY - lidT, -hw], [hoodX1, hoodFrontY - lidT, hw], [hoodX0, hoodRearY - lidT, hw]],
    [[hoodX0, hoodRearY, -hw], [hoodX1, hoodFrontY, -hw], [hoodX1, hoodFrontY, hw], [hoodX0, hoodRearY, hw]],
  ), 'hood');
  if (!hasHood) return;

  // ── the recess lining ──────────────────────────────────────────────────────
  // ⚠ THE BAY HAS NO FLAT FLOOR, and assuming one is the trap here. `emitRun`
  // detours the shell's floor line OVER the front wheel arch (that detour is
  // what stops you seeing through the arch into the body), so a slab at
  // `bayFloor` would have a body-coloured hump growing straight through it —
  // measured across 480 specs, the crown stands 250-320 mm above the bay floor
  // and leaves only 50-96 mm under the bonnet, and on a big-wheeled pickup it
  // passes the BONNET LINE by up to 150 mm. So the lining is a RIBBON that
  // follows the same envelope the profile walks, 10 mm proud of it, and clamped
  // 20 mm under the panel: where the arch is high the ribbon is buried inside
  // the shell's own solid (invisible, and it can never poke through a shut
  // hood); where the bay is deep it floats over the floor and is the dark
  // surface you look down onto.
  //
  // The SIDES are deliberately left alone: those are the fenders' inner faces,
  // and an inner wing is body-coloured on a real car too. Six quads at most —
  // an end wall is dropped when the arch already fills that end — so twelve
  // triangles against the 62 the worst shipped car has left in the budget.
  const hz = W / 2 - 0.075;
  const under = (x) => hoodTopY(LY, x) - lidT - 0.020;
  /** @param {number} x @returns {number} Lining height at car x. */
  const shelfY = (x) => {
    const d = Math.abs(x - fa);
    const arch = d < archRdF ? g + Math.sqrt(archRdF * archRdF - d * d) : -Infinity;
    return Math.max(bayFloor + 0.010, Math.min(Math.max(bayFloor, arch) + 0.010, under(x)));
  };
  const SEG = 4;
  const xa = (i) => bayX0 + ((bayX1 - bayX0) * i) / SEG;
  /** @type {Array<{n: number[], q: Array<number[]>}>} */
  const faces = [];
  for (let i = 0; i < SEG; i++) {
    const x0 = xa(i);
    const x1 = xa(i + 1);
    const y0 = shelfY(x0);
    const y1 = shelfY(x1);
    // Sampling the arc at the segment ENDS chords it, which puts the ribbon
    // UNDER the true arc — inside solid shell, never through it. That is the
    // safe direction, and it is why the sampling can stay this coarse.
    faces.push({ n: [0, 1, 0], q: [[x0, y0, -hz], [x1, y1, -hz], [x1, y1, hz], [x0, y0, hz]] });
  }
  // Firewall and slam panel: the bay's two end walls, inset 10 mm off the
  // shell's own so nothing is coplanar with anything.
  for (const [x, dir] of [[bayX0 + 0.010, 1], [bayX1 - 0.010, -1]]) {
    const y0 = shelfY(x);
    const y1 = under(x);
    if (y1 - y0 < 0.02) continue; // no room: the arch already fills this end
    faces.push({ n: [dir, 0, 0], q: [[x, y0, -hz], [x, y1, -hz], [x, y1, hz], [x, y0, hz]] });
  }
  // NOT an occluder: one AABB per piece would make this hollow lining a solid
  // block in the nose and re-shade the wheels of a car with its bonnet SHUT.
  // See `CarAcc#addNonOccluding` for the measurement.
  acc.addNonOccluding('body', linerGeo(faces), A.dark);
  void s;
}

/**
 * The rear third: boot / bed / cargo walls, the (shut) lid or gate, the
 * backlight, and the D pillars.
 * @param {CarSpec} s @param {ReturnType<carLayout>} LY @param {any} A @param {CarAcc} acc
 */
function emitRear(s, LY, A, acc) {
  const { W, g, belt, roof, floorY, tail, ra, archR, archRd, archTopY, lidT, cabTopHalf,
    glassW0, dzR, trunkF, cargoF, cabRear, roofRear, deckY, tubFloor, cargoFloor, bedFloor } = LY;

  /**
   * Interior side wall split around the rear arch, so no slab hangs down into
   * the wheel opening and no triangular gap opens beside it.
   * @param {number} x0 @param {number} x1 @param {number} y0 @param {number} y1
   * @param {number} z @param {number} d
   */
  function sideWall(x0, x1, y0, y1, z, d) {
    // Flat boxes run to where the arch LIP crosses y0 (not the arch's ground
    // footprint): stopping at ra +/- archRd let the chorded bottom rise off y0
    // before the shell lip did, opening a triangular gap on each side.
    const dyc = Math.min(archRd, Math.max(0, y0 - g));
    const dxc = Math.sqrt(archRd * archRd - dyc * dyc);
    const a0 = ra - dxc;
    const a1 = ra + dxc;
    if (x0 < Math.min(a0, x1) - 0.01 && y1 - y0 > 0.01) {
      acc.paint('body', boxGeo(Math.min(a0, x1) - x0, y1 - y0, d, (x0 + Math.min(a0, x1)) / 2, (y0 + y1) / 2, z), 'shell');
    }
    if (x1 > Math.max(a1, x0) + 0.01 && y1 - y0 > 0.01) {
      acc.paint('body', boxGeo(x1 - Math.max(a1, x0), y1 - y0, d, (Math.max(a1, x0) + x1) / 2, (y0 + y1) / 2, z), 'shell');
    }
    const m0 = Math.max(x0, a0);
    const m1 = Math.min(x1, a1);
    if (m0 >= m1) return;
    const botY = (x) => {
      const dd = Math.abs(x - ra);
      return dd >= dxc ? y0 : Math.max(y0, g + Math.sqrt(archRd * archRd - dd * dd) - 0.02);
    };
    const sd = Math.sign(z);
    const zo = sd * (Math.abs(z) + d / 2 - 0.001);
    const zi = sd * (Math.abs(z) - d / 2);
    const N = 6;
    for (let k = 0; k < N; k++) {
      const xa = m0 + ((m1 - m0) * k) / N;
      const xb = m0 + ((m1 - m0) * (k + 1)) / N;
      const yA = botY(xa);
      const yB = botY(xb);
      if (y1 - Math.min(yA, yB) < 0.015) continue;
      acc.paint('body', solidGeo(
        [[xa, yA, zo], [xb, yB, zo], [xb, y1, zo], [xa, y1, zo]],
        [[xa, yA, zi], [xb, yB, zi], [xb, y1, zi], [xa, y1, zi]],
      ), 'shell');
    }
  }

  const lw = W / 2 - 0.01;

  if (s.type === 'sedan') {
    for (const sd of [-1, 1]) sideWall(tail + 0.12, trunkF + 0.02, tubFloor, deckY - lidT, sd * (W / 2 - 0.035), 0.07);
    // Boot lid, SHUT.
    acc.paint('body', solidGeo(
      [[trunkF + 0.05, deckY - lidT, -lw], [tail + 0.005, deckY - lidT, -lw], [tail + 0.005, deckY - lidT, lw], [trunkF + 0.05, deckY - lidT, lw]],
      [[trunkF + 0.05, deckY, -lw], [tail + 0.005, deckY, -lw], [tail + 0.005, deckY, lw], [trunkF + 0.05, deckY, lw]],
    ), 'shell');
    acc.add('glass', solidGeo(
      [[trunkF, deckY + 0.01, -glassW0], [trunkF, deckY + 0.01, glassW0], [roofRear, roof + 0.005, cabTopHalf - 0.04], [roofRear, roof + 0.005, -(cabTopHalf - 0.04)]],
      [[trunkF + 0.035, deckY + 0.01, -glassW0], [trunkF + 0.035, deckY + 0.01, glassW0], [roofRear + 0.035, roof + 0.005, cabTopHalf - 0.04], [roofRear + 0.035, roof + 0.005, -(cabTopHalf - 0.04)]],
    ), A.glass);
  } else if (s.type === 'pickup') {
    const bedFront = cabRear + 0.06;
    for (const sd of [-1, 1]) sideWall(tail, bedFront, bedFloor, belt, sd * (W / 2 - 0.035), 0.07);
    acc.paint('body', boxGeo(0.05, belt - bedFloor - 0.02, W - 0.16, tail - 0.005, (belt + bedFloor) / 2, 0), 'shell');
    acc.paint('body', boxGeo(0.06, belt - archTopY, W - 0.1, cabRear, (belt + archTopY) / 2, 0), 'shell');
    acc.paint('body', boxGeo(0.06, archTopY - floorY, W - 0.16, cabRear, (archTopY + floorY) / 2, 0), 'shell');
    acc.add('glass', solidGeo(
      [[cabRear + 0.01, belt, -(cabTopHalf - 0.02)], [cabRear + 0.01, belt, cabTopHalf - 0.02], [cabRear + 0.01, roof - 0.03, cabTopHalf - 0.05], [cabRear + 0.01, roof - 0.03, -(cabTopHalf - 0.05)]],
      [[cabRear - 0.015, belt, -(cabTopHalf - 0.02)], [cabRear - 0.015, belt, cabTopHalf - 0.02], [cabRear - 0.015, roof - 0.03, cabTopHalf - 0.05], [cabRear - 0.015, roof - 0.03, -(cabTopHalf - 0.05)]],
    ), A.glass);
  } else {
    // wagon / suv / van / hatchback: cargo walls + a top-hinged tailgate, SHUT.
    for (const sd of [-1, 1]) sideWall(tail, cargoF, cargoFloor, belt, sd * (W / 2 - 0.035), 0.07);
    const tilt = s.type === 'hatchback';
    const gBot = tilt ? tail : tail - 0.01;
    // Hatch glass edges must bury inside the sloped sails (flush edges opened a
    // sky slot); boxy types get a fully VERTICAL pane, because a sloped one
    // exits the vertical D-pillar and opens a triangular side gap.
    const gw0 = W / 2 - 0.075;
    const gwT = tilt ? cabTopHalf + 0.005 : cabTopHalf - 0.03;
    const gTopF = tilt ? roofRear + 0.01 : tail + 0.025;
    const gBotF = tilt ? gBot : tail + 0.025;
    const gTopR = tilt ? roofRear - 0.025 : tail - 0.005;
    const gBotR = tilt ? gBot - 0.03 : tail - 0.005;
    acc.add('glass', solidGeo(
      [[gBotF, belt + 0.01, -gw0], [gBotF, belt + 0.01, gw0], [gTopF, roof + 0.003, gwT], [gTopF, roof + 0.003, -gwT]],
      [[gBotR, belt + 0.01, -gw0], [gBotR, belt + 0.01, gw0], [gTopR, roof + 0.003, gwT], [gTopR, roof + 0.003, -gwT]],
    ), A.glass);
    acc.paint('body', solidGeo(
      [[gBot + 0.01, belt + 0.02, -(W / 2 - 0.04)], [gBot + 0.01, belt + 0.02, W / 2 - 0.04], [tail + 0.01, cargoFloor + 0.005, W / 2 - 0.04], [tail + 0.01, cargoFloor + 0.005, -(W / 2 - 0.04)]],
      [[gBot - 0.045, belt + 0.02, -(W / 2 - 0.04)], [gBot - 0.045, belt + 0.02, W / 2 - 0.04], [tail - 0.045, cargoFloor + 0.005, W / 2 - 0.04], [tail - 0.045, cargoFloor + 0.005, -(W / 2 - 0.04)]],
    ), 'shell');
    acc.add('body', boxGeo(0.02, 0.035, 0.22, tail - 0.055, belt + 0.09, 0), A.chrome);
    acc.add('body', boxGeo(0.02, 0.13, 0.3, tail - 0.05, cargoFloor + (belt - cargoFloor) * 0.35, 0), A.plate);

    // D pillars. The hatchback gets a sloped sail panel following the gate line
    // (a vertical post at the tail floated in mid-air behind the sloped hatch).
    if (tilt) {
      const sx = Math.max(tail + 0.16, roofRear + 0.02) + 0.045;
      for (const sd of [-1, 1]) {
        acc.paint('body', solidGeo(
          [[tail + 0.02, belt - 0.01, sd * (W / 2 - 0.03)], [sx, belt - 0.01, sd * (W / 2 - 0.03)], [sx, roof + 0.01, sd * (cabTopHalf + 0.02)], [roofRear + 0.02, roof + 0.01, sd * (cabTopHalf + 0.02)]],
          [[tail + 0.02, belt - 0.01, sd * (W / 2 - 0.10)], [sx, belt - 0.01, sd * (W / 2 - 0.10)], [sx, roof + 0.01, sd * (cabTopHalf - 0.05)], [roofRear + 0.02, roof + 0.01, sd * (cabTopHalf - 0.05)]],
        ), 'shell');
      }
    } else {
      // Rear edge at tail-0.015 so the vertical glass plane is buried in the pillar.
      for (const sd of [-1, 1]) {
        acc.paint('body', solidGeo(
          [[tail - 0.015, belt, sd * (W / 2 - 0.03)], [tail + 0.13, belt, sd * (W / 2 - 0.03)], [tail + 0.13, roof + 0.01, sd * (cabTopHalf + 0.02)], [tail - 0.015, roof + 0.01, sd * (cabTopHalf + 0.02)]],
          [[tail - 0.015, belt, sd * (W / 2 - 0.10)], [tail + 0.13, belt, sd * (W / 2 - 0.10)], [tail + 0.13, roof + 0.01, sd * (cabTopHalf - 0.05)], [tail - 0.015, roof + 0.01, sd * (cabTopHalf - 0.05)]],
        ), 'shell');
      }
    }
  }
}

/**
 * Windscreen, A pillars, roof, B/door posts, vent and quarter glass, sails.
 * @param {CarSpec} s @param {ReturnType<carLayout>} LY @param {any} A @param {CarAcc} acc
 */
function emitGreenhouse(s, LY, A, acc) {
  const { W, belt, roof, tail, cowl, cabTopHalf, roofFront, roofRear, dzF, dzR, bpX, trunkF, cabRear } = LY;
  const slopeX = (y) => cowl + (roofFront - cowl) * (y - belt) / (roof - belt);

  // Windscreen. Side edges run out to the VENT-GLASS plane, not to glassW0: a
  // narrower pane left an open wedge behind the A-pillar that showed sky.
  acc.add('glass', solidGeo(
    [[cowl, belt + 0.005, -(W / 2 - 0.055)], [cowl, belt + 0.005, W / 2 - 0.055], [roofFront, roof + 0.005, cabTopHalf + 0.005], [roofFront, roof + 0.005, -(cabTopHalf + 0.005)]],
    [[cowl - 0.035, belt + 0.005, -(W / 2 - 0.055)], [cowl - 0.035, belt + 0.005, W / 2 - 0.055], [roofFront - 0.035, roof + 0.005, cabTopHalf + 0.005], [roofFront - 0.035, roof + 0.005, -(cabTopHalf + 0.005)]],
  ), A.glass);

  // A pillars: the rear edge extends 50 mm behind the windscreen plane so the
  // glass side edge is buried (flush edge-to-edge showed a sky sliver), and the
  // foot drops to belt-0.07 so it meets the hood top with no notch.
  for (const sd of [-1, 1]) {
    acc.paint('body', solidGeo(
      [[cowl - 0.05, belt - 0.07, sd * (W / 2 - 0.03)], [cowl + 0.09, belt - 0.07, sd * (W / 2 - 0.03)], [roofFront + 0.09, roof + 0.01, sd * (cabTopHalf + 0.02)], [roofFront - 0.05, roof + 0.01, sd * (cabTopHalf + 0.02)]],
      [[cowl - 0.05, belt - 0.07, sd * (W / 2 - 0.11)], [cowl + 0.09, belt - 0.07, sd * (W / 2 - 0.11)], [roofFront + 0.09, roof + 0.01, sd * (cabTopHalf - 0.06)], [roofFront - 0.05, roof + 0.01, sd * (cabTopHalf - 0.06)]],
    ), 'shell');
  }

  acc.paint('body', solidGeo(
    [[roofRear - 0.02, roof, -(cabTopHalf + 0.045)], [roofFront + 0.05, roof, -(cabTopHalf + 0.045)], [roofFront + 0.05, roof, cabTopHalf + 0.045], [roofRear - 0.02, roof, cabTopHalf + 0.045]],
    [[roofRear - 0.02, roof + 0.05, -(cabTopHalf + 0.01)], [roofFront + 0.05, roof + 0.05, -(cabTopHalf + 0.01)], [roofFront + 0.05, roof + 0.05, cabTopHalf + 0.01], [roofRear - 0.02, roof + 0.05, cabTopHalf + 0.01]],
  ), 'roof');

  // B pillar + door edge posts. The B pillar spans from behind the rear-door
  // glass edge to the front-door edge — the old 0.05-wide post left an open slit
  // on its rear side. These stay BLACK (real cars do), so `aPaint` is 0.
  /** @type {Array<[number, number]>} */
  const posts = [];
  if (bpX != null) posts.push([bpX - 0.062, 0.112]);
  posts.push([dzF + 0.005, 0.05]);
  posts.push([dzR - 0.055, 0.05]);
  for (const [px, pw] of posts) {
    if (px > roofFront + 0.05 || px < roofRear) continue;
    for (const sd of [-1, 1]) {
      acc.add('body', solidGeo(
        [[px, belt - 0.01, sd * (W / 2 - 0.04)], [px + pw, belt - 0.01, sd * (W / 2 - 0.04)], [px + pw, roof + 0.005, sd * (cabTopHalf + 0.015)], [px, roof + 0.005, sd * (cabTopHalf + 0.015)]],
        [[px, belt - 0.01, sd * (W / 2 - 0.085)], [px + pw, belt - 0.01, sd * (W / 2 - 0.085)], [px + pw, roof + 0.005, sd * (cabTopHalf - 0.03)], [px, roof + 0.005, sd * (cabTopHalf - 0.03)]],
      ), A.dark);
    }
  }

  // Front vent glass (the triangle between the door front chord and the A pillar).
  const ymTop = roof + 0.01;
  if (cowl - dzF > 0.08) {
    const xTop = slopeX(ymTop);
    for (const sd of [-1, 1]) {
      const zb = sd * (W / 2 - 0.055);
      const zt = sd * (cabTopHalf + 0.005);
      acc.add('glass', solidGeo(
        [[dzF - 0.005, belt, zb], [cowl - 0.01, belt, zb], [xTop - 0.01, ymTop, zt], [xTop - 0.012, ymTop, zt]],
        [[dzF - 0.005, belt, zb + sd * -0.02], [cowl - 0.01, belt, zb + sd * -0.02], [xTop - 0.01, ymTop, zt + sd * -0.02], [xTop - 0.012, ymTop, zt + sd * -0.02]],
      ), A.glass);
    }
  }

  if (s.type === 'sedan') {
    for (const sd of [-1, 1]) {
      acc.paint('body', solidGeo(
        [[trunkF + 0.02, belt - 0.01, sd * (W / 2 - 0.045)], [dzR - 0.005, belt - 0.01, sd * (W / 2 - 0.045)], [dzR - 0.005, roof + 0.005, sd * (cabTopHalf + 0.015)], [roofRear + 0.02, roof + 0.005, sd * (cabTopHalf + 0.015)]],
        [[trunkF + 0.02, belt - 0.01, sd * (W / 2 - 0.12)], [dzR - 0.005, belt - 0.01, sd * (W / 2 - 0.12)], [dzR - 0.005, roof + 0.005, sd * (cabTopHalf - 0.06)], [roofRear + 0.02, roof + 0.005, sd * (cabTopHalf - 0.06)]],
      ), 'shell');
    }
  } else if (s.type === 'pickup') {
    for (const sd of [-1, 1]) {
      acc.paint('body', solidGeo(
        [[cabRear - 0.03, belt - 0.01, sd * (W / 2 - 0.04)], [dzR - 0.005, belt - 0.01, sd * (W / 2 - 0.04)], [dzR - 0.005, roof + 0.005, sd * (cabTopHalf + 0.015)], [roofRear, roof + 0.005, sd * (cabTopHalf + 0.015)]],
        [[cabRear - 0.03, belt - 0.01, sd * (W / 2 - 0.10)], [dzR - 0.005, belt - 0.01, sd * (W / 2 - 0.10)], [dzR - 0.005, roof + 0.005, sd * (cabTopHalf - 0.04)], [roofRear, roof + 0.005, sd * (cabTopHalf - 0.04)]],
      ), 'shell');
    }
  } else {
    const qx0 = Math.max(tail + 0.16, roofRear + 0.02);
    const qx1 = dzR - 0.06;
    if (qx1 - qx0 > 0.2) {
      const segs = (s.type === 'van' && qx1 - qx0 > 1.0) ? 2 : 1;
      for (const sd of [-1, 1]) {
        const zb = sd * (W / 2 - 0.055);
        const zt = sd * (cabTopHalf + 0.005);
        acc.add('glass', solidGeo(
          [[qx0, belt, zb], [qx1, belt, zb], [qx1, roof - 0.04, zt], [qx0, roof - 0.04, zt]],
          [[qx0, belt, zb - sd * 0.02], [qx1, belt, zb - sd * 0.02], [qx1, roof - 0.04, zt - sd * 0.02], [qx0, roof - 0.04, zt - sd * 0.02]],
        ), A.glass);
        if (segs === 2) {
          const mx = (qx0 + qx1) / 2;
          acc.add('body', solidGeo(
            [[mx, belt - 0.01, sd * (W / 2 - 0.045)], [mx + 0.05, belt - 0.01, sd * (W / 2 - 0.045)], [mx + 0.05, roof, sd * (cabTopHalf + 0.01)], [mx, roof, sd * (cabTopHalf + 0.01)]],
            [[mx, belt - 0.01, sd * (W / 2 - 0.08)], [mx + 0.05, belt - 0.01, sd * (W / 2 - 0.08)], [mx + 0.05, roof, sd * (cabTopHalf - 0.025)], [mx, roof, sd * (cabTopHalf - 0.025)]],
          ), A.dark);
        }
      }
    }
  }
}

/**
 * Doors. Each is its own part; its window glass is baked OPAQUE and dark into
 * the door so the car never carries two transparent layers.
 * @param {CarSpec} s @param {ReturnType<carLayout>} LY @param {any} A @param {CarAcc} acc
 */
function emitDoors(s, LY, A, acc) {
  const { W, belt, roof, floorY, cowl, cabTopHalf, roofFront, dzF } = LY;
  const slopeX = (y) => cowl + (roofFront - cowl) * (y - belt) / (roof - belt);
  const ymTop = roof + 0.01;

  for (const d of LY.doors) {
    for (const sd of [-1, 1]) {
      const part = `door_${d.key}${sd < 0 ? 'l' : 'r'}`;
      const panelW = d.x1 - d.x0 - 0.012;
      const pcx = (d.x0 + d.x1) / 2;
      acc.paint(part, boxGeo(panelW, belt - floorY - 0.03, 0.05, pcx, (belt + floorY) / 2, sd * (W / 2 - 0.017)), 'door');
      // Beltline sill: closes the see-through slit between the glass plane and
      // the panel top (the shell is tub-cut here, so nothing else fills it).
      acc.paint(part, boxGeo(panelW, 0.035, 0.088, pcx, belt - 0.0125, sd * (W / 2 - 0.036)), 'door');
      acc.add(part, boxGeo(panelW - 0.04, belt - floorY - 0.07, 0.02, pcx, (belt + floorY) / 2, sd * (W / 2 - 0.055)), A.inter);
      acc.add(part, boxGeo(0.14, 0.035, 0.02, d.x0 + 0.14, belt - 0.12, sd * (W / 2 + 0.012)), A.chrome);
      if (s.x.doorTrim) acc.add(part, boxGeo(panelW, 0.035, 0.012, pcx, belt - 0.28, sd * (W / 2 + 0.012)), A.dark);

      const wx0 = d.x0 + 0.02;
      const wx1 = d.x1 - 0.025;
      const zb = sd * (W / 2 - 0.055);
      const zt = sd * (cabTopHalf + 0.005);
      let xtf = wx1;
      if (d.key === 'f') xtf = Math.min(wx1, slopeX(ymTop) - 0.035);
      acc.add(part, solidGeo(
        [[wx0, belt - 0.005, zb], [wx1, belt - 0.005, zb], [xtf, ymTop, zt], [wx0, ymTop, zt]],
        [[wx0, belt - 0.005, zb - sd * 0.02], [wx1, belt - 0.005, zb - sd * 0.02], [xtf, ymTop, zt - sd * 0.02], [wx0, ymTop, zt - sd * 0.02]],
      ), A.doorGlass);
    }
  }
  void dzF;
}

/**
 * Bumpers, lamps, grille, plates. This is where the `lamps` part is populated.
 * @param {CarSpec} s @param {ReturnType<carLayout>} LY @param {any} A @param {CarAcc} acc
 */
function emitFascia(s, LY, A, acc) {
  const { W, g, nose, tail, hoodFrontY, lidT, rearTopY } = LY;
  const bmpH = 0.17;
  const bmpY = g + 0.095;

  acc.add('body', boxGeo(0.17, bmpH, W + 0.08, nose + 0.06, bmpY, 0), A.bumper);
  acc.add('body', boxGeo(0.17, bmpH, W + 0.08, tail - 0.06, bmpY, 0), A.bumper);
  for (const sd of [-1, 1]) {
    acc.add('body', boxGeo(0.3, bmpH, 0.05, nose - 0.14, bmpY, sd * (W / 2 + 0.015)), A.bumper);
    acc.add('body', boxGeo(0.3, bmpH, 0.05, tail + 0.14, bmpY, sd * (W / 2 + 0.015)), A.bumper);
  }

  const faceTop = hoodFrontY - lidT;
  const bmpTop = bmpY + bmpH / 2;
  const lightY = bmpTop + (faceTop - bmpTop) * 0.62;

  if (s.x.quadLights) {
    for (const sd of [-1, 1]) {
      acc.add('lamps', boxGeo(0.03, 0.11, 0.1, nose + 0.005, lightY, sd * (W / 2 - 0.14)), A.lightF, LAMP.HEAD);
      acc.add('lamps', boxGeo(0.03, 0.11, 0.1, nose + 0.005, lightY, sd * (W / 2 - 0.27)), A.lightF, LAMP.HEAD);
    }
  } else {
    for (const sd of [-1, 1]) {
      acc.add('lamps', boxGeo(0.03, 0.12, 0.24, nose + 0.005, lightY, sd * (W / 2 - 0.22)), A.lightF, LAMP.HEAD);
    }
  }
  for (const sd of [-1, 1]) {
    acc.add('lamps', boxGeo(0.03, 0.08, 0.09, nose + 0.005, lightY - 0.005, sd * (W / 2 - 0.05)), A.signal,
      sd < 0 ? LAMP.SIGNAL_L : LAMP.SIGNAL_R);
  }

  const grw = Math.min(0.7, W * 0.36);
  acc.add('body', boxGeo(0.04, 0.15, grw, nose + 0.005, lightY, 0), A.dark);
  for (let i = 0; i < 3; i++) {
    acc.add('body', boxGeo(0.012, 0.014, grw - 0.04, nose + 0.028, lightY - 0.045 + i * 0.045, 0), A.rim);
  }
  if (s.x.grilleChrome) {
    acc.add('body', boxGeo(0.02, 0.02, grw + 0.04, nose + 0.02, lightY + 0.085, 0), A.chrome);
    acc.add('body', boxGeo(0.02, 0.02, grw + 0.04, nose + 0.02, lightY - 0.085, 0), A.chrome);
  }
  acc.add('body', boxGeo(0.02, 0.13, 0.3, nose + 0.15, bmpY, 0), A.plate);

  const tlY = (s.type === 'sedan' || s.type === 'pickup')
    ? rearTopY - 0.12
    : Math.max(rearTopY - 0.1, bmpTop + 0.09);
  for (const sd of [-1, 1]) {
    acc.add('lamps', boxGeo(0.03, 0.11, 0.22, tail - 0.005, tlY, sd * (W / 2 - 0.2)), A.lightR, LAMP.BRAKE);
    acc.add('lamps', boxGeo(0.03, 0.09, 0.08, tail - 0.005, tlY, sd * (W / 2 - 0.05)), A.signal,
      sd < 0 ? LAMP.SIGNAL_L : LAMP.SIGNAL_R);
  }
  if (s.type === 'sedan' || s.type === 'pickup') {
    acc.add('body', boxGeo(0.02, 0.13, 0.3, tail - 0.15, bmpY, 0), A.plate);
  }
}

/**
 * Rocker, exhaust, mirrors, antenna, roof rack. Everything bolted on outside.
 * @param {CarSpec} s @param {ReturnType<carLayout>} LY @param {any} A @param {CarAcc} acc
 */
function emitFurniture(s, LY, A, acc) {
  const { W, g, belt, roof, tail, cowl, cabTopHalf, roofFront, roofRear, dzF, dzR, hoodRearY } = LY;

  if (s.x.rocker) {
    for (const sd of [-1, 1]) {
      acc.add('body', boxGeo(dzF - dzR, 0.09, 0.03, (dzF + dzR) / 2, g + 0.065, sd * (W / 2 - 0.005)), A.dark);
    }
  }
  acc.add('body', cylGeo(0.032, 0.2, tail - 0.05, g + 0.03, W * 0.28, 'x', 8, true), A.dark);

  for (const sd of [-1, 1]) {
    // The arm reaches inboard past the vent-glass plane so the mirror anchors to
    // the body rather than floating beside it.
    acc.paint('body', boxGeo(0.03, 0.02, 0.16, dzF + 0.02, belt + 0.06, sd * (W / 2 + 0.005)), 'shell');
    acc.paint('body', boxGeo(0.04, 0.1, 0.15, dzF + 0.02, belt + 0.11, sd * (W / 2 + 0.1)), 'shell');
  }

  if (s.x.antenna) {
    acc.add('body', cylGeo(0.008, 0.38, cowl + 0.12, hoodRearY + 0.16, -(W / 2 - 0.1), 'y', 5, true, 0.22), A.dark);
  }

  if (s.x.rack) {
    const rkLen = (roofFront - roofRear) - 0.3;
    const rkCx = (roofFront + roofRear) / 2;
    for (const sd of [-1, 1]) {
      acc.add('body', boxGeo(rkLen, 0.045, 0.055, rkCx, roof + 0.095, sd * (cabTopHalf - 0.08)), A.dark);
      for (const fx of [-rkLen / 2 + 0.1, rkLen / 2 - 0.1]) {
        acc.add('body', boxGeo(0.05, 0.05, 0.04, rkCx + fx, roof + 0.06, sd * (cabTopHalf - 0.08)), A.dark);
      }
    }
    for (let i = 0; i < 3; i++) {
      acc.add('body', boxGeo(0.05, 0.035, (cabTopHalf - 0.05) * 2, rkCx - rkLen / 2 + 0.15 + i * (rkLen - 0.3) / 2, roof + 0.1, 0), A.dark);
    }
    if (s.x.roofBox) {
      acc.paint('body', boxGeo(Math.min(1.2, rkLen - 0.3), 0.28, 0.62, rkCx - 0.1, roof + 0.26, 0), 'shell');
    }
  }
}

/**
 * The steering wheel PART (rim + spokes, authored in the column's own frame) and
 * the steering COLUMN (rigid, so it goes into the body).
 *
 * The part's rest rotation is upstream's `swTilt · sw` — a −0.45 rad tilt about
 * Z, then the −90 degree yaw that stands the wheel up. Decomposed to an XYZ
 * Euler it is nowhere near gimbal lock (`sin(ry) ≈ −0.90`), so it round-trips
 * exactly; the fleet then spins the part about its LOCAL Z, which is the column
 * axis by construction.
 *
 * @param {CarSpec} s @param {ReturnType<carLayout>} LY @param {any} A @param {CarAcc} acc
 * @returns {{x:number,y:number,z:number,rx:number,ry:number,rz:number}} rest
 */
function emitSteering(s, LY, A, acc) {
  const { W, belt, dzF } = LY;
  const swPos = new THREE.Vector3(dzF - 0.32, belt, -W * 0.24);

  // ── the spinning part, in the column frame ────────────────────────────────
  // LOD: upstream's 5x12 torus is 120 triangles for an object seen through a
  // windscreen at 52 degrees of pitch. 4x10 is 80 and reads identically.
  acc.add('steering_wheel', flat(new THREE.TorusGeometry(0.165, 0.022, 4, 10)), A.interDark);
  for (let i = 0; i < 3; i++) {
    acc.add('steering_wheel', boxGeo(0.3, 0.025, 0.02, 0, 0, 0, (i * Math.PI) / 3), A.interDark);
  }

  // ── the column, welded into the body ──────────────────────────────────────
  const swRotM = new THREE.Matrix4().makeRotationZ(-0.45).multiply(new THREE.Matrix4().makeRotationY(-Math.PI / 2));
  const swM = swRotM.clone().setPosition(swPos);
  const col = cylGeo(0.025, 0.3, 0, 0, 0, 'y', 6, true);
  col.rotateX(Math.PI / 2);
  col.translate(0, 0, -0.15);
  col.applyMatrix4(swM);
  acc.add('body', col, A.interDark);

  const e = new THREE.Euler().setFromRotationMatrix(swRotM, 'XYZ');
  return { x: swPos.x, y: swPos.y, z: swPos.z, rx: e.x, ry: e.y, rz: e.z };
}

/**
 * THE wheel — one geometry for all four (see the header). Authored for the
 * RIGHT side with the hub at the local origin and the rim dished toward +Z.
 * @param {CarSpec} s @param {ReturnType<carLayout>} LY @param {any} A @param {CarAcc} acc
 */
function emitWheel(s, LY, A, acc) {
  const r = s.wheelR;
  const w = s.wheelW;
  // LOD, and the single biggest lever on the per-car budget: upstream ran
  // 16/12/6 segments and up to 8 spoke bars, which is 232 triangles — times FOUR
  // wheels, that alone was a third of the car. A wheel is ~30 px at gameplay
  // range, so 12/8/5 segments and at most 6 bars (each bar spans the diameter,
  // so 6 bars read as TWELVE spokes) costs <=172 and nothing survives the pixel
  // grid. `spec.spokes` still varies 5..6 of them, so wheels are not clones.
  acc.add('wheel', cylGeo(r, w, 0, 0, 0, 'z', 12), A.tire);
  acc.add('wheel', cylGeo(r * 0.56, w * 0.7, 0, 0, w * 0.16, 'z', 8), A.rim);
  const bars = Math.min(6, s.spokes);
  for (let i = 0; i < bars; i++) {
    acc.add('wheel', boxGeo(0.02, r * 0.92, 0.02, 0, 0, w * 0.52, (i * Math.PI) / bars), A.dark);
  }
  acc.add('wheel', cylGeo(r * 0.12, 0.02, 0, 0, w * 0.54, 'z', 5), A.dark);
  void LY;
}

/**
 * The police / SWAT lightbar: a dark plinth welded to the body plus two lens
 * boxes on siren channels. Red sits on the LEFT (−Z, the driver's side) and blue
 * on the right, so a car seen from behind reads red-then-blue left to right.
 * @param {CarSpec} s @param {ReturnType<carLayout>} LY @param {any} A @param {CarAcc} acc
 */
function emitLightbar(s, LY, A, acc) {
  const { roof, cabTopHalf, roofFront, roofRear } = LY;
  const cx = roofRear + (roofFront - roofRear) * 0.62;
  const y0 = roof + 0.05; // the roof solid's upper surface
  const len = Math.min(1.05, (cabTopHalf + 0.045) * 2 - 0.12);
  const half = len / 2;

  acc.add('body', boxGeo(0.17, 0.035, len, cx, y0 + 0.0175, 0), A.dark);
  acc.add('lamps', boxGeo(0.115, 0.075, half - 0.02, cx, y0 + 0.035 + 0.0375, -(half / 2)), A.sirenRed, LAMP.SIREN_RED);
  acc.add('lamps', boxGeo(0.115, 0.075, half - 0.02, cx, y0 + 0.035 + 0.0375, half / 2), A.sirenBlue, LAMP.SIREN_BLUE);
  void s;
}
